import { useEffect, useState, type ReactNode } from "react";
import {
  AccentColorPicker,
  Button,
  Select,
  ThemeToggle,
  Toggle,
  initialsOf,
  vars,
} from "@pack-it-up/ui-web";
import type { ThemeMode } from "@pack-it-up/ui-web";
import { useStoreValue } from "@pack-it-up/viewmodel/bindings";
import type { Theme } from "@pack-it-up/viewmodel";
import type { AuthProvider, ItemDisplay } from "@pack-it-up/core";
import { useStores } from "../../app/AppContext.js";
import { useSession } from "../../app/SessionProvider.js";
import { useMySettings } from "../../app/useMySettings.js";
import {
  ClockIcon13,
  GlobeIcon,
  GoogleIcon,
  InvitesIcon,
  LockIcon,
  PackingTabIcon,
  PaletteIcon,
  PersonIcon,
  RowsIcon,
  SignOutArrowIcon,
  SunIcon,
} from "../../app/icons.js";
import { ChangePasswordModal } from "./ChangePasswordModal.js";
import { EditNameModal } from "./EditNameModal.js";
import { SignOutModal } from "./SignOutModal.js";
import { hourOptions, timezoneOptions } from "./settingsOptions.js";

// ─── layout primitives ─────────────────────────────────────────────────────────

const SettingsSection = ({
  title,
  sub,
  children,
}: {
  title: string;
  sub: string;
  children: ReactNode;
}): ReactNode => (
  <div style={{ marginBottom: "28px" }}>
    <div
      style={{
        fontFamily: vars.font.heading,
        fontWeight: vars.font.weightBold,
        fontSize: vars.font.sizeSm,
        letterSpacing: "-0.1px",
        marginBottom: "4px",
      }}
    >
      {title}
    </div>
    <div style={{ fontSize: vars.font.sizeXs, color: vars.color.textMuted, marginBottom: "14px" }}>
      {sub}
    </div>
    <div
      style={{
        background: vars.color.surface,
        border: `1px solid ${vars.color.border}`,
        borderRadius: "12px",
        overflow: "hidden",
      }}
    >
      {children}
    </div>
  </div>
);

const SettingRow = ({
  icon,
  name,
  desc,
  children,
  onClick,
}: {
  icon: ReactNode;
  name: string;
  desc: string;
  children?: ReactNode;
  onClick?: () => void;
}): ReactNode => (
  <div
    onClick={onClick}
    style={{
      display: "flex",
      alignItems: "center",
      gap: "12px",
      padding: "14px 16px",
      cursor: onClick !== undefined ? "pointer" : undefined,
    }}
    // Adjacent rows get a border-top via a wrapping trick; we use a CSS class approach
    // with inline style separator divs instead.
  >
    <div
      style={{
        width: "30px",
        height: "30px",
        borderRadius: "8px",
        background: vars.color.surfaceRaised,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: vars.color.textMuted,
        flexShrink: 0,
      }}
    >
      {icon}
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: vars.font.sizeSm, fontWeight: vars.font.weightMedium }}>{name}</div>
      <div style={{ fontSize: "11px", color: vars.color.textMuted, marginTop: "2px" }}>{desc}</div>
    </div>
    {children}
  </div>
);

// A thin rule between rows
const RowDivider = (): ReactNode => (
  <div style={{ height: "1px", background: vars.color.border, margin: "0" }} />
);

// ─── The main page ─────────────────────────────────────────────────────────────

// The settings screen (Doc 04 §6–7): profile card, account settings, appearance,
// and sign out. Friends and managed profiles live on the Friends page (its own nav
// entry) as tabs, not duplicated here.
export const SettingsPage = (): ReactNode => {
  const { auth, theme } = useStores();
  const { inviteInbox } = useSession();

  const user = useStoreValue(auth, (s) => s.user);
  const authError = useStoreValue(auth, (s) => s.error);
  const mode = useStoreValue(theme, (s) => s.theme) as ThemeMode;
  const accentColor = useStoreValue(theme, (s) => s.accentColor);

  const { profile, settings, ready, save } = useMySettings();

  const providers = user?.providers ?? [];
  const hasPassword = providers.includes("password");

  const [timezone, setTimezone] = useState("");
  const [nameModalOpen, setNameModalOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [signOutModalOpen, setSignOutModalOpen] = useState(false);
  const [sendingVerification, setSendingVerification] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [checkingVerification, setCheckingVerification] = useState(false);

  useEffect(() => {
    if (settings !== null) {
      setTimezone(settings.timezone);
    }
  }, [settings]);

  const onTimezoneChange = (tz: string): void => {
    setTimezone(tz);
    if (ready) {
      void save({ timezone: tz, reminderSendHour: settings?.reminderSendHour ?? 7 });
    }
  };

  const onReminderHourChange = (h: string): void => {
    if (ready) void save({ reminderSendHour: Number(h) });
  };

  const onDefaultStagingOptInChange = (value: boolean): void => {
    if (ready) void save({ defaultStagingOptIn: value });
  };

  const onItemDisplayChange = (value: string): void => {
    if (ready) void save({ itemDisplay: value as ItemDisplay });
  };

  // Link when the provider isn't on the account yet, unlink when it is. The store
  // mirrors the updated user (and any error copy, e.g. last-method) into state.
  const toggleProvider = async (provider: AuthProvider, providerId: string): Promise<void> => {
    if (providers.includes(providerId)) await auth.getState().unlinkProvider(providerId);
    else await auth.getState().linkProvider(provider);
  };

  const setTheme = (m: ThemeMode): void => void theme.getState().setTheme(m as Theme);
  const setAccentColor = (color: string | null): void => void theme.getState().setAccentColor(color);

  const onSendVerification = async (): Promise<void> => {
    setSendingVerification(true);
    const r = await auth.getState().sendEmailVerification();
    setSendingVerification(false);
    if (r.ok) setVerificationSent(true);
  };

  // Firebase's auth-state stream doesn't re-fire once the emailed link is clicked, so
  // this re-checks explicitly. On success, also re-run the invite inbox — it was
  // skipping listMyInvites while unverified (T25's callable requires it server-side).
  const onCheckVerification = async (): Promise<void> => {
    setCheckingVerification(true);
    const r = await auth.getState().refreshCurrentUser();
    setCheckingVerification(false);
    if (r.ok && r.value.emailVerified) void inviteInbox.getState().refresh();
  };

  // The profile doc is the live source for the name (it re-emits on save); the Auth
  // record is the fallback before the first snapshot lands.
  const displayName = profile?.displayName ?? user?.displayName ?? "";
  const email = profile?.email !== undefined && profile.email !== "" ? profile.email : (user?.email ?? "");
  const initials = displayName !== "" ? initialsOf(displayName) : "?";

  return (
    <div
      style={{
        flex: 1,
        overflowY: "auto",
        padding: "32px 28px 60px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <div style={{ width: "100%", maxWidth: "480px" }}>
        {/* Profile card */}
        <div style={{ display: "flex", alignItems: "center", gap: "20px", marginBottom: "32px" }}>
          <div
            style={{
              width: "72px",
              height: "72px",
              borderRadius: "50%",
              background: "linear-gradient(135deg, #f0a500, #ef4444)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: vars.font.heading,
              fontWeight: vars.font.weightHeavy,
              fontSize: "24px",
              color: "#fff",
              flexShrink: 0,
            }}
            aria-hidden
          >
            {initials}
          </div>
          <div>
            <div
              style={{
                fontFamily: vars.font.heading,
                fontWeight: vars.font.weightBold,
                fontSize: vars.font.sizeXl,
                letterSpacing: "-0.3px",
              }}
            >
              {displayName}
            </div>
            {email !== "" && (
              <div style={{ fontSize: vars.font.sizeSm, color: vars.color.textMuted, marginTop: "4px" }}>
                {email}
              </div>
            )}
          </div>
        </div>

        {/* Account section */}
        <SettingsSection title="Account" sub="Your profile, security, and regional preferences.">
          <SettingRow icon={PersonIcon} name="Name" desc="The name your friends and trip mates see">
            <Button
              variant="ghost"
              size="sm"
              aria-label="Change name"
              onClick={() => setNameModalOpen(true)}
              style={{ marginLeft: "auto", flexShrink: 0 }}
            >
              Change
            </Button>
          </SettingRow>
          <RowDivider />
          {user !== null && !user.isAnonymous && (
            <>
              <SettingRow
                icon={InvitesIcon}
                name="Email verification"
                desc={user.emailVerified ? "Verified" : "Verify your email so invites can find you"}
              >
                {!user.emailVerified && (
                  <div style={{ display: "flex", gap: "8px", marginLeft: "auto", flexShrink: 0 }}>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label="Check verification status"
                      onClick={() => void onCheckVerification()}
                      disabled={checkingVerification}
                    >
                      {checkingVerification ? "Checking…" : "Refresh"}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label="Resend verification email"
                      onClick={() => void onSendVerification()}
                      disabled={sendingVerification}
                    >
                      {verificationSent ? "Sent" : "Resend"}
                    </Button>
                  </div>
                )}
              </SettingRow>
              <RowDivider />
            </>
          )}
          {/* Provider-only accounts get "Set" — adds a password credential so the
              OAuth provider can later be unlinked. */}
          <SettingRow
            icon={LockIcon}
            name="Password"
            desc={hasPassword ? "Change your account password" : "Set a password to sign in with email"}
          >
            <Button
              variant="ghost"
              size="sm"
              aria-label={hasPassword ? "Change password" : "Set password"}
              onClick={() => setPasswordModalOpen(true)}
              style={{ marginLeft: "auto", flexShrink: 0 }}
            >
              {hasPassword ? "Change" : "Set"}
            </Button>
          </SettingRow>
          <RowDivider />
          <SettingRow icon={GlobeIcon} name="Timezone" desc="Used for reminders and countdowns">
            <div style={{ marginLeft: "auto", flexShrink: 0 }}>
              {!ready ? (
                <span style={{ fontSize: vars.font.sizeXs, color: vars.color.textMuted }}>Loading…</span>
              ) : (
                <Select
                  label="Timezone"
                  hideLabel
                  value={timezone}
                  onChange={onTimezoneChange}
                  options={timezoneOptions(settings?.timezone)}
                />
              )}
            </div>
          </SettingRow>
          <RowDivider />
          <SettingRow icon={ClockIcon13} name="Reminder time" desc="Local hour for to-do reminder emails">
            <div style={{ marginLeft: "auto", flexShrink: 0 }}>
              {!ready ? (
                <span style={{ fontSize: vars.font.sizeXs, color: vars.color.textMuted }}>Loading…</span>
              ) : (
                <Select
                  label="Reminder time"
                  hideLabel
                  value={String(settings?.reminderSendHour ?? 7)}
                  onChange={onReminderHourChange}
                  options={hourOptions}
                />
              )}
            </div>
          </SettingRow>
          <RowDivider />
          <SettingRow icon={PackingTabIcon} name="Stage items by default" desc="New trips start with the staged-packing step on for you">
            <div style={{ marginLeft: "auto", flexShrink: 0 }}>
              {!ready ? (
                <span style={{ fontSize: vars.font.sizeXs, color: vars.color.textMuted }}>Loading…</span>
              ) : (
                <Toggle
                  checked={settings?.defaultStagingOptIn ?? false}
                  onChange={onDefaultStagingOptInChange}
                  label="Stage items by default"
                  hideLabel
                />
              )}
            </div>
          </SettingRow>
          <RowDivider />
          <SettingRow icon={RowsIcon} name="Participant details" desc="Applies to every trip — how each person shows on a row">
            <div style={{ marginLeft: "auto", flexShrink: 0 }}>
              {!ready ? (
                <span style={{ fontSize: vars.font.sizeXs, color: vars.color.textMuted }}>Loading…</span>
              ) : (
                <Select
                  label="Participant details"
                  hideLabel
                  value={settings?.itemDisplay ?? "condensed"}
                  onChange={onItemDisplayChange}
                  options={[
                    { value: "condensed", label: "Collapsed" },
                    { value: "expanded", label: "Expanded" },
                  ]}
                />
              )}
            </div>
          </SettingRow>
        </SettingsSection>

        {/* Sign-in methods */}
        <SettingsSection title="Sign-in methods" sub="Ways you can sign in to your account.">
          <SettingRow icon={GoogleIcon} name="Google" desc={providers.includes("google.com") ? "Linked" : "Not linked"}>
            <Button
              variant="ghost"
              size="sm"
              aria-label={providers.includes("google.com") ? "Unlink Google" : "Link Google"}
              onClick={() => void toggleProvider({ kind: "google" }, "google.com")}
              style={{ marginLeft: "auto", flexShrink: 0 }}
            >
              {providers.includes("google.com") ? "Unlink" : "Link"}
            </Button>
          </SettingRow>
          {authError !== null && (
            <div style={{ padding: "0 16px 12px", fontSize: vars.font.sizeXs, color: vars.color.danger }}>
              {authError}
            </div>
          )}
        </SettingsSection>

        {/* Appearance section */}
        <SettingsSection title="Appearance" sub="Choose how PackItUp looks on this device.">
          <SettingRow icon={SunIcon} name="Theme" desc="Light, dark, or match your system">
            <div style={{ marginLeft: "auto", flexShrink: 0 }}>
              <ThemeToggle value={mode} onChange={setTheme} />
            </div>
          </SettingRow>
          <RowDivider />
          <SettingRow icon={PaletteIcon} name="Accent color" desc="Personalize buttons, highlights, and links">
            <div style={{ marginLeft: "auto", flexShrink: 0 }}>
              <AccentColorPicker value={accentColor} onChange={setAccentColor} />
            </div>
          </SettingRow>
        </SettingsSection>

        {/* Sign out */}
        <button
          type="button"
          onClick={() => setSignOutModalOpen(true)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "10px 16px",
            borderRadius: "10px",
            border: "1px solid rgba(239, 68, 68, 0.35)",
            background: "transparent",
            color: vars.color.danger,
            fontFamily: vars.font.body,
            fontSize: vars.font.sizeSm,
            fontWeight: vars.font.weightMedium,
            cursor: "pointer",
          }}
        >
          {SignOutArrowIcon}
          Sign out
        </button>
      </div>

      {/* Modals */}
      <EditNameModal open={nameModalOpen} currentName={displayName} onClose={() => setNameModalOpen(false)} />
      <ChangePasswordModal open={passwordModalOpen} hasPassword={hasPassword} onClose={() => setPasswordModalOpen(false)} />
      <SignOutModal open={signOutModalOpen} onClose={() => setSignOutModalOpen(false)} />
    </div>
  );
};
